import { Action, createReducer } from '@ngrx/store';
import { HttpStateHandler } from '@shared/utils/classes/http-state-handler';
import { HttpState } from '@shared/utils/models/http.model';
import { produceOn } from '@shared/utils/functions/store.function';
import { PolicyData } from '@core/utils/models/policy.model';
import { PolicyActions, PolicyResetActions } from '../actions/policy.action';

export const policyFeatureKey = 'policy';

export interface PolicyState {
  data: PolicyData | null;
  httpState: HttpState;
}

export interface State {
  userAgreement: PolicyState;
  termsOfUse: PolicyState;
  privacyPolicy: PolicyState;
}

const initialPolicyState = { data: null, httpState: HttpStateHandler.initialState };

export const initialState: State = {
  userAgreement: initialPolicyState,
  termsOfUse: initialPolicyState,
  privacyPolicy: initialPolicyState,
};

export const policyReducer = createReducer(
  initialState,
  /** user agreement */
  produceOn(PolicyActions.getUserAgreement, (draft, action) => {
    draft.userAgreement.data = initialState.userAgreement.data;
    draft.userAgreement.httpState = HttpStateHandler.loading(draft.userAgreement.httpState, action);
  }),
  produceOn(PolicyActions.getUserAgreementSuccess, (draft, action) => {
    draft.userAgreement.data = action.data;
    draft.userAgreement.httpState = HttpStateHandler.success(draft.userAgreement.httpState, action);
  }),
  produceOn(PolicyActions.getUserAgreementFailure, (draft, action) => {
    draft.userAgreement.data = initialState.userAgreement.data;
    draft.userAgreement.httpState = HttpStateHandler.error(draft.userAgreement.httpState, action);
  }),
  /** terms of use */
  produceOn(PolicyActions.getTermsOfUse, (draft, action) => {
    draft.termsOfUse.data = initialState.termsOfUse.data;
    draft.termsOfUse.httpState = HttpStateHandler.loading(draft.termsOfUse.httpState, action);
  }),
  produceOn(PolicyActions.getTermsOfUseSuccess, (draft, action) => {
    draft.termsOfUse.data = action.data;
    draft.termsOfUse.httpState = HttpStateHandler.success(draft.termsOfUse.httpState, action);
  }),
  produceOn(PolicyActions.getTermsOfUseFailure, (draft, action) => {
    draft.termsOfUse.data = initialState.termsOfUse.data;
    draft.termsOfUse.httpState = HttpStateHandler.error(draft.termsOfUse.httpState, action);
  }),
  /** privacy policy */
  produceOn(PolicyActions.getPrivacyPolicy, (draft, action) => {
    draft.privacyPolicy.data = initialState.privacyPolicy.data;
    draft.privacyPolicy.httpState = HttpStateHandler.loading(draft.privacyPolicy.httpState, action);
  }),
  produceOn(PolicyActions.getPrivacyPolicySuccess, (draft, action) => {
    draft.privacyPolicy.data = action.data;
    draft.privacyPolicy.httpState = HttpStateHandler.success(draft.privacyPolicy.httpState, action);
  }),
  produceOn(PolicyActions.getPrivacyPolicyFailure, (draft, action) => {
    draft.privacyPolicy.data = initialState.privacyPolicy.data;
    draft.privacyPolicy.httpState = HttpStateHandler.error(draft.privacyPolicy.httpState, action);
  }),
  /** reset actions */
  produceOn(PolicyResetActions.cancelPolicyActionsSuccess, (draft) => {
    Object.keys(draft).forEach((entityName) => {
      draft[entityName as keyof State].httpState = HttpStateHandler.initialState;
    });
  }),
  produceOn(PolicyResetActions.resetPolicy, (draft) => {
    Object.keys(draft).forEach((entityName) => {
      draft[entityName as keyof State] = initialPolicyState;
    });
  })
);

export function reducer(state: State | undefined, action: Action) {
  return policyReducer(state, action);
}
